import { HasPermissionDirective } from '@/app/core/directives/has-permission.directive';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { TCtaKpiStatus } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EPlatformFeature } from '@enums/campaign.enum';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { CreateCampaignDialogComponent } from '@pages/campaigns/create-campaign-dialog/create-campaign-dialog.component';
import { BaseComponent } from '@shared/components/base.component';
import { IconBadgeListComponent } from '@shared/components/icon-badge-list/icon-badge-list.component';
import { IIconData } from '@shared/components/icon-badge-list/icon-badge-list.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { ProgressBarComponent } from '@shared/components/progress-bar/progress-bar.component';
import { SocialBadgeListComponent } from '@shared/components/social-badge-list/social-badge-list.component';
import { IBadgeData } from '@shared/components/social-badge-list/social-badge-list.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  endOfDay,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { TimeagoModule } from 'ngx-timeago';
import { v4 as uuidv4 } from 'uuid';

@Component({
  imports: [
    TranslateModule,
    MatButtonModule,
    IconBadgeListComponent,
    ProgressBarComponent,
    SocialBadgeListComponent,
    IconComponent,
    IconBadgeComponent,
    StatusBadgeComponent,
    NgxSkeletonLoaderModule,
    MatTooltipModule,
    TextClampTooltipDirective,
    TimeagoModule,
    HasPermissionDirective,
  ],
  selector: 'app-dashboard-campaigns',
  styleUrl: './dashboard-campaigns.component.scss',
  templateUrl: './dashboard-campaigns.component.html',
})
export class DashboardCampaignsComponent extends BaseComponent implements AfterViewInit {
  public readonly campaigns = input.required<TCampaignApi[]>();
  public readonly selectedDate = input.required<Date>();

  public readonly campaignAnalyticsMap = input<Record<string, ICampaignAnalytics>>({});
  public readonly loadingCampaigns = input<boolean>(false);
  public readonly loadingCampaignAnalytics = input<boolean>(false);

  public readonly createCampaignClick = output<void>();

  @ViewChild('scrollContainer', { static: false })
  private scrollContainer?: ElementRef<HTMLDivElement>;

  public readonly canScrollLeft = signal<boolean>(false);
  public readonly canScrollRight = signal<boolean>(false);

  public readonly filteredCampaigns = computed(() => {
    const campaigns = this.campaigns();

    return campaigns.filter((campaign) =>
      isWithinInterval(startOfDay(this.selectedDate()), {
        end: endOfDay(campaign.end),
        start: startOfDay(campaign.start),
      }),
    );
  });

  public readonly selectedDateLabel = computed(() => {
    return this.dateUtils.formatDate(this.selectedDate(), 'EEEE, MMMM d, yyyy');
  });

  public readonly campaignPlatformsMap = computed(() => {
    const campaignPlatformsMap = new Map<string, Set<EPlatformFeature>>();
    const badgeDataMap = new Map<string, IBadgeData[]>();
    const campaigns = this.campaigns();

    campaigns.forEach((campaign) => {
      campaign.ctaTemplates?.forEach((cta) => {
        const existingCampaignPlatforms = campaignPlatformsMap.get(campaign.id) ?? new Set<EPlatformFeature>();

        existingCampaignPlatforms.add(cta.platformFeature);
        campaignPlatformsMap.set(campaign.id, existingCampaignPlatforms);
      });
    });

    campaignPlatformsMap.forEach((platforms, campaignId) => {
      badgeDataMap.set(
        campaignId,
        Array.from(platforms).map((platform) => ({
          id: uuidv4(),
          platform,
          platformCreatorId: '',
        })),
      );
    });

    return badgeDataMap;
  });

  public readonly campaignTimeRemainingMap = computed(() => {
    const campaignTimeRemainingMap = new Map<string, string | null>();
    const campaigns = this.campaigns();

    campaigns.forEach((campaign) => {
      campaignTimeRemainingMap.set(campaign.id, this.getTimeRemaining(campaign));
    });

    return campaignTimeRemainingMap;
  });

  public readonly dummyCampaigns = Array.from({ length: 5 }, () => ({ id: uuidv4() }) as TCampaignApi);

  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);

  constructor() {
    super();

    effect(() => {
      if (this.loadingCampaigns()) {
        return;
      }

      this.filteredCampaigns();

      requestAnimationFrame(() => {
        this.onScroll();
      });
    });
  }

  public getTimeRemaining(campaign: TCampaignApi): string | null {
    const campaignEnd = campaign.end as Date;

    if (!campaignEnd) {
      return '';
    }

    const now = new Date();
    const minutes = differenceInMinutes(campaignEnd, now);
    const hours = differenceInHours(campaignEnd, now);
    const days = differenceInDays(campaignEnd, now);

    if (minutes < 0 || hours < 0 || days < 0) {
      return null;
    }

    const translationPrefix = 'shared.timeSuffixLong';

    if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? this.translate.instant(`${translationPrefix}.minuteSingular`) : this.translate.instant(`${translationPrefix}.minutesPlural`)}`;
    }

    if (hours < 24) {
      return `${hours} ${hours === 1 ? this.translate.instant(`${translationPrefix}.hourSingular`) : this.translate.instant(`${translationPrefix}.hoursPlural`)}`;
    }

    return `${days} ${days === 1 ? this.translate.instant(`${translationPrefix}.daySingular`) : this.translate.instant(`${translationPrefix}.daysPlural`)}`;
  }

  public getCreatorsIconData(campaign: TCampaignApi): IIconData[] {
    if (!campaign.campaignCreators) {
      return [];
    }

    return campaign.campaignCreators.reduce((acc: IIconData[], cc) => {
      if (cc.creator) {
        acc.push({
          alt: cc.creator.name ?? '',
          content: cc.creator.icon ?? '',
          id: parseInt(cc.creator.id.slice(0, 8) ?? '', 16),
          name: cc.creator.name ?? '',
        });
      }

      return acc;
    }, []);
  }

  public getPlatformsBadgeData(campaign: TCampaignApi): IBadgeData[] {
    if (!campaign.ctaTemplates) {
      return [];
    }

    const uniquePlatforms = new Set<EPlatformFeature>();

    campaign.ctaTemplates.forEach((cta) => {
      if (cta.platformFeature) {
        uniquePlatforms.add(cta.platformFeature);
      }
    });

    return Array.from(uniquePlatforms).map((platform) => ({
      id: uuidv4(),
      platform,
      platformCreatorId: '',
    }));
  }

  public getCampaignProgress(campaign: TCampaignApi): TCtaKpiStatus {
    const analytics = this.campaignAnalyticsMap()[campaign.id];

    if (!analytics) {
      return { color: '', current: 0, status: EAnalyticsStatus.processing, total: 0 };
    }

    const {
      currentOkDeliverables,
      currentProblemDeliverables,
      currentOkKpis,
      currentProblemKpis,
      expectedDeliverables,
      expectedKpis,
    } = analytics.ctaKpiStatus;

    const current =
      (currentOkDeliverables ?? 0) +
      (currentProblemDeliverables ?? 0) +
      (currentOkKpis ?? 0) +
      (currentProblemKpis ?? 0);

    return {
      ...this.campaignAnalyticsUtilities.getStatusableDisplayConfig(analytics.status),
      current,
      total: (expectedDeliverables ?? 0) + (expectedKpis ?? 0),
    };
  }

  public onScrollLeft(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollBy({ behavior: 'smooth', left: -400 });
    }
  }

  public onScrollRight(): void {
    if (this.scrollContainer) {
      this.scrollContainer.nativeElement.scrollBy({ behavior: 'smooth', left: 400 });
    }
  }

  public onScroll(): void {
    if (!this.scrollContainer) {
      return;
    }

    const container = this.scrollContainer.nativeElement;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;
    const canScrollLeft = scrollLeft > 10;
    const canScrollRight = maxScrollLeft > 10 && scrollLeft < maxScrollLeft - 10;

    this.canScrollLeft.set(canScrollLeft);
    this.canScrollRight.set(canScrollRight);
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.onScroll();
    }, 100);
  }

  public onCreateCampaignClick(): void {
    this.dialog.open(CreateCampaignDialogComponent, {
      autoFocus: true,
      data: {
        campaign: null,
      },
      minWidth: '700px',
    });
  }

  public openCampaignInNewTab(campaignId: string, event: Event): void {
    event.stopPropagation();
    const url = this.router.serializeUrl(this.router.createUrlTree(['/campaigns', campaignId]));
    WindowRedirectService.openUrlInNewTab(url);
  }

  public getProgressBarWidth(campaignId: string): number {
    const badgeCount = this.campaignPlatformsMap().get(campaignId)?.length ?? 0;

    if (badgeCount < 2) {
      return 182;
    }

    if (badgeCount < 3) {
      return 162;
    }

    return 142;
  }
}
